// Centralized icon components. Color is `currentColor` (Tailwind `text-*`).
// PNG glyphs are CSS-masked (same pattern as Pencil / Trash). Spinner stays
// inline SVG so it can animate.

/** PNG masked with currentColor. Size: `size` (px), else Tailwind w/h on
 *  className, else `defaultSize`, else 1em. */
function MaskedIcon({
  src,
  className = "",
  size,
  defaultSize,
}: {
  src: string;
  className?: string;
  size?: number;
  defaultSize?: number | [number, number];
}) {
  const classHasBox = /\b(?:w-|h-|size-)/.test(className);
  let dim: { width: number | string; height: number | string } | undefined;
  if (size != null) dim = { width: size, height: size };
  else if (!classHasBox) {
    if (Array.isArray(defaultSize)) dim = { width: defaultSize[0], height: defaultSize[1] };
    else dim = { width: defaultSize ?? "1em", height: defaultSize ?? "1em" };
  }

  return (
    <span
      role="img"
      aria-hidden="true"
      className={`inline-block shrink-0 align-middle bg-current ${className}`}
      style={{
        ...dim,
        WebkitMask: `url(${src}) center / contain no-repeat`,
        mask: `url(${src}) center / contain no-repeat`,
      }}
    />
  );
}

export function Lock({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/lock.png" className={className} defaultSize={14} />;
}

export function LockOpen({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/lock-open.png" className={className} defaultSize={14} />;
}

export function Envelope({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/envelope.png" className={className} defaultSize={18} />;
}

export function Plus({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/plus-stroke.png" className={className} defaultSize={16} />;
}

/** Circle with a plus — the one add glyph (`greenCirclePlus.png`). Always green. */
export function CirclePlus({ className = "" }: { className?: string }) {
  return <MaskedIcon src="/icons/greenCirclePlus.png" className={`text-green ${className}`} defaultSize={16} />;
}

export function StatusDot({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/status-dot.png" className={className} defaultSize={16} />;
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={`animate-spin-indicator ${className ?? ""}`}
      width={16}
      height={16}
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="25.13"
        strokeDashoffset="8"
      />
    </svg>
  );
}

export function CheckCircle({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/check-circle.png" className={className} defaultSize={16} />;
}

/** Empty circle — CheckCircle off-state for check-toggles. */
export function CheckCircleOff({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/check-circle-off.png" className={className} defaultSize={16} />;
}

export function Download({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/download.png" className={className} defaultSize={12} />;
}

export function Gear({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/gear.png" className={className} defaultSize={14} />;
}

export function Terminal({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/terminal.png" className={className} defaultSize={18} />;
}

export function ChevronRight({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/chevron-right.png" className={className} defaultSize={14} />;
}

export function ChevronLeft({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/chevron-right.png" className={`rotate-180 ${className ?? ""}`} defaultSize={14} />;
}

export function ChartIcon({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/chart.png" className={className} defaultSize={14} />;
}

export function Target({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/target.png" className={className} defaultSize={13} />;
}

export function Eye({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/eye.png" className={className} defaultSize={14} />;
}

/**
 * Edit icon = project PNG `/icons/edit.png` (pencil on document), masked so it
 * takes `currentColor`. Use `text-amber` for the standard yellow edit control.
 */
export function Pencil({ className = "" }: { className?: string }) {
  return (
    <span
      role="img"
      aria-hidden="true"
      className={`inline-block shrink-0 align-middle bg-current ${className}`}
      style={{
        width: "1em",
        height: "1em",
        WebkitMask: "url(/icons/edit.png) center / contain no-repeat",
        mask: "url(/icons/edit.png) center / contain no-repeat",
      }}
    />
  );
}

export function Exit({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/exit.png" className={className} defaultSize={14} />;
}

export function Refresh({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/refresh.png" className={className} defaultSize={14} />;
}

export function Reconnect({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/reconnect.png" className={className} defaultSize={18} />;
}

export function Scan({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/search.png" className={className} defaultSize={14} />;
}

/**
 * Delete icon = project PNG `/icons/trash_icon.png`, masked so it takes
 * `currentColor`. Use `text-red` for the standard red trash control.
 */
/** Default 18×18. Pass `size` for denser row actions. No margin/padding. */
export function Trash({ className = "", size = 18 }: { className?: string; size?: number }) {
  return (
    <span
      role="img"
      aria-hidden="true"
      className={`block shrink-0 bg-current ${className}`}
      style={{
        width: size,
        height: size,
        WebkitMask: "url(/icons/trash_icon.png) center / contain no-repeat",
        mask: "url(/icons/trash_icon.png) center / contain no-repeat",
      }}
    />
  );
}

export function ChevronDown({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/chevron-down.png" className={className} defaultSize={12} />;
}

export function ChevronUp({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/chevron-down.png" className={`rotate-180 ${className ?? ""}`} defaultSize={12} />;
}

export function XCircle({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/x-circle.png" className={className} defaultSize={18} />;
}

/** Six-dot grip for drag-to-reorder list rows. */
export function Grip({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/grip.png" className={className} defaultSize={14} />;
}

export function LinkIcon({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/link.png" className={className} defaultSize={16} />;
}

/** Document / statement — used on All Trades month-end rows. */
export function FileText({ className, size = 14 }: { className?: string; size?: number }) {
  return <MaskedIcon src="/icons/file-text.png" className={className} size={size} />;
}

/** Circle-? help glyph. Default gray matches the previous CSS border/? color. */
export function CircleQuestion({ className = "" }: { className?: string }) {
  return (
    <MaskedIcon
      src="/icons/circle-question.png"
      className={`text-[#808080] ${className}`}
      defaultSize={14}
    />
  );
}

export function Upload({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/upload.png" className={className} defaultSize={18} />;
}

export function Play({ className }: { className?: string }) {
  return <MaskedIcon src="/icons/play.png" className={className} defaultSize={18} />;
}
